'use client'

import { useGroups, useSubscriptions } from './queries'

export interface TariffProduct {
	id: number
	name: string
	price: number
	new_price?: number
	duration_days: number
	type: string
	available: boolean
	benefits?: string[]
}

export interface SubItem {
	id: string
	status: number
	is_active: boolean
	virtual_subscription: boolean
	subscription_price: number
	start_at: string
	expires_at: string
	group_product: {
		id: number
		name: string
		products: TariffProduct[]
	}
}

const DAY = 86_400_000
export const effectivePrice = (p: TariffProduct) => p.new_price ?? p.price

/** Сопоставляет подписку с продуктом каталога: сперва по цене, затем по длительности. */
function matchProduct(
	products: TariffProduct[],
	sub: SubItem | undefined
): TariffProduct | undefined {
	if (!sub) return undefined
	const byPrice = products.find(p => effectivePrice(p) === sub.subscription_price)
	if (byPrice) return byPrice
	const dur = Math.round(
		(new Date(sub.expires_at).getTime() - new Date(sub.start_at).getTime()) / DAY
	)
	return products.find(p => p.duration_days === dur)
}

/**
 * Единое состояние смены тарифа из общего кэша (подписки + каталог групп).
 * Используется и страницей подписок (гейтинг кнопки/нотис), и страницей смены тарифа.
 *
 * - activeSub — действующая подписка (status=1 + is_active, не виртуальная).
 *   В будущем групп может быть несколько; пока берём первую активную.
 * - products — тарифы её группы из каталога /groups (в подписке встроен только
 *   текущий продукт, поэтому каталог обязателен для списка альтернатив).
 * - scheduledSub / scheduledProduct — запланированное понижение: неактивная
 *   (status=0) подписка в той же группе, что активируется со следующего периода.
 */
export function useTariffState() {
	const subs = useSubscriptions()
	const groups = useGroups()

	const items = (subs.data?.items ?? []) as SubItem[]
	const groupItems = (groups.data?.items ?? []) as any[]

	// Действующая подписка определяется по is_active, а НЕ по status: при
	// запланированном понижении текущая подписка имеет status=3 (auto-renew
	// перенесён на новый тариф), но остаётся is_active=true до конца периода.
	// Среди нескольких is_active берём самую свежую по start_at — после апгрейдов
	// текущим тарифом является последний созданный (старые могут остаться is_active).
	const activeSub =
		[...items]
			.filter(s => s.is_active && !s.virtual_subscription)
			.sort(
				(a, b) =>
					new Date(b.start_at).getTime() - new Date(a.start_at).getTime()
			)[0] || items.find(s => s.is_active)

	const catalogGroup = groupItems.find(
		g => g.id === activeSub?.group_product?.id
	)
	const products: TariffProduct[] =
		catalogGroup?.products ?? activeSub?.group_product?.products ?? []

	const currentProduct = matchProduct(products, activeSub)
	const currentEffective = currentProduct
		? effectivePrice(currentProduct)
		: activeSub?.subscription_price ?? 0

	// Запланированный переход на другой тариф = неактивная (is_active=false)
	// подписка со status=0 и start_at в будущем. Пока он есть — сменить тариф нельзя.
	const now = Date.now()
	const scheduledSub = activeSub
		? items.find(
				s =>
					!s.is_active &&
					s.status === 0 &&
					new Date(s.start_at).getTime() > now &&
					s.group_product?.id === activeSub.group_product?.id
		  )
		: undefined
	const scheduledProduct = scheduledSub
		? matchProduct(products, scheduledSub) ??
		  scheduledSub.group_product?.products?.[0]
		: undefined

	return {
		isLoading: subs.isLoading,
		groupsLoading: groups.isLoading,
		items,
		activeSub,
		products,
		currentProduct,
		currentEffective,
		scheduledSub,
		scheduledProduct,
		hasScheduledDowngrade: !!scheduledSub,
	}
}
